export interface FilterOption {
  Value: string;
  Name: string;
}

export interface TaxIdOption {
  Tin: string;
  Tinname: string;
  checked?: boolean;
}

export interface NpiOption {
  Value: string;
  Name: string;
}

export interface FilterState {
  timePeriod?: string;
  timePeriodText?: string;
  lineOfBusiness?: string | string[];
  providerSetting?: string;
  taxId?: TaxIdOption[];
  contractId?: FilterOption[];
  npi?: NpiOption | NpiOption[];
  specialty?: FilterOption[];
  channel?: string;
  serviceSetting?: string;
  serviceCategory?: string;
  region?: string;
  state?: string | string[];
  market?: string | string[];
  groups?: string;
}

// Single page type for Prior Auth
export type PageName = 'priorAuth';

export interface FilterConfig {
  page: PageName;
  enabledFilters: string[];
  defaultValues: FilterState;
}

export interface FilterEventDetail {
  filters: FilterState;
  pageName: PageName;
}

// ─────────────────────────────────────────────────────────
//  Filter Dropdown Options
// ─────────────────────────────────────────────────────────

const currentYear = new Date().getFullYear();

// Matches PAQuickFilter getTimePeriodOptions() — excludes Rolling3Months per PageTimeFrameMap.default
export const TIME_PERIOD_OPTIONS = [
  { value: 'Last30Days', label: 'Last 30 Days' },
  { value: 'Last3Months', label: 'Last 3 Months' },
  { value: 'Last6Months', label: 'Last 6 Months' },
  { value: 'Last12Months', label: 'Last 12 Months' },
  { value: 'YTD', label: 'Year to Date' },
  { value: (currentYear - 1).toString(), label: (currentYear - 1).toString() },
  { value: (currentYear - 2).toString(), label: (currentYear - 2).toString() },
];

export const LOB_OPTIONS = [
  { value: 'All', label: 'All Lines of Business' },
  { value: 'EI', label: 'Commercial' },
  { value: 'MR', label: 'Medicare' },
  { value: 'CS', label: 'Medicaid' },
  { value: 'EXC', label: 'Individual Exchange' },
  { value: 'UN', label: 'Uncategorized', disabled: true },
];

// Values MUST match Angular ProviderSetting in filter-options.ts
export const PROVIDER_SETTING_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'AD', label: 'Admitting Services' },
  { value: 'AT', label: 'Attending Physician' },
  { value: 'FA', label: 'Facility' },
  { value: 'PC', label: 'Primary Care Provider' },
  { value: 'RF', label: 'Requesting Provider' },
  { value: 'SJ', label: 'Servicing' },
];

// Values MUST match Angular Channel in filter-options.ts
export const CHANNEL_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'EDI', label: 'EDI' },
  { value: 'RHYME', label: 'RHYME' },
  { value: 'FAX', label: 'FAX' },
  { value: 'PAAN', label: 'PAAN' },
  { value: 'PHONE', label: 'PHONE' },
  { value: 'OTHER', label: 'OTHER' },
];

// Values MUST match Angular ServiceSetting in filter-options.ts
export const SERVICE_TYPE_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'INPATIENT', label: 'Inpatient' },
  { value: 'OUTPATIENT', label: 'Outpatient' },
  { value: 'OUTPATIENT_FACILITY', label: 'Outpatient Facility' },
];

// Groups options
export const GROUPS_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'EMPIRE', label: 'Empire' },
];

// Hardcoded master list matching Angular ServiceCategory in filter-options.ts:192
// Used as initial dropdown options; API autocomplete (SERVICECATEGORY) enriches the list
export const SERVICE_CATEGORY_OPTIONS = [
  { value: 'All', label: 'All' },
  { value: 'Behavioral Health', label: 'Behavioral Health' },
  { value: 'Cardiac', label: 'Cardiac' },
  { value: 'Cardiac Surgery', label: 'Cardiac Surgery' },
  { value: 'Cardiology', label: 'Cardiology' },
  { value: 'Chiropractic', label: 'Chiropractic' },
  { value: 'DME', label: 'DME' },
  { value: 'Dental', label: 'Dental' },
  { value: 'Dermatology', label: 'Dermatology' },
  { value: 'Dialysis', label: 'Dialysis' },
  { value: 'ENT', label: 'ENT' },
  { value: 'Endocrinology', label: 'Endocrinology' },
  { value: 'Eye Care', label: 'Eye Care' },
  { value: 'GI', label: 'GI' },
  { value: 'General Surgery', label: 'General Surgery' },
  { value: 'Genetics', label: 'Genetics' },
  { value: 'Gynecology', label: 'Gynecology' },
  { value: 'Hematology', label: 'Hematology' },
  { value: 'Home Health', label: 'Home Health' },
  { value: 'Imaging', label: 'Imaging' },
  { value: 'Infectious Disease', label: 'Infectious Disease' },
  { value: 'Infusion Therapy', label: 'Infusion Therapy' },
  { value: 'Inpatient', label: 'Inpatient' },
  { value: 'Lab', label: 'Lab' },
  { value: 'Maternity', label: 'Maternity' },
  { value: 'Medical Oncology', label: 'Medical Oncology' },
  { value: 'Musculoskeletal', label: 'Musculoskeletal' },
  { value: 'Nephrology', label: 'Nephrology' },
  { value: 'Neurology', label: 'Neurology' },
  { value: 'Neurosurgery', label: 'Neurosurgery' },
  { value: 'Nursing Facility', label: 'Nursing Facility' },
  { value: 'Obstetrics', label: 'Obstetrics' },
  { value: 'Occupational Therapy', label: 'Occupational Therapy' },
  { value: 'Oncology', label: 'Oncology' },
  { value: 'Oral Surgery', label: 'Oral Surgery' },
  { value: 'Orthopedic Surgery', label: 'Orthopedic Surgery' },
  { value: 'Other', label: 'Other' },
  { value: 'PT/OT/ST', label: 'PT/OT/ST' },
  { value: 'Pain Management', label: 'Pain Management' },
  { value: 'Physical Therapy', label: 'Physical Therapy' },
  { value: 'Plastic Surgery', label: 'Plastic Surgery' },
  { value: 'Podiatry', label: 'Podiatry' },
  { value: 'Pulmonology', label: 'Pulmonology' },
  { value: 'Radiation Oncology', label: 'Radiation Oncology' },
  { value: 'Reconstructive Surgery', label: 'Reconstructive Surgery' },
  { value: 'Rehabilitation', label: 'Rehabilitation' },
  { value: 'Rheumatology', label: 'Rheumatology' },
  { value: 'Sleep', label: 'Sleep' },
  { value: 'Speech Therapy', label: 'Speech Therapy' },
  { value: 'Spine Surgery', label: 'Spine Surgery' },
  { value: 'Transplant', label: 'Transplant' },
  { value: 'Urology', label: 'Urology' },
  { value: 'Vascular Surgery', label: 'Vascular Surgery' },
];

// Default filter values — values must match PAQuickFilter codes
export const DEFAULT_FILTERS = {
  timePeriod: 'Last12Months',
  lineOfBusiness: [] as string[],
  providerSetting: 'RF',
  channel: 'All',
  serviceSetting: 'All',
  serviceCategory: 'All',
  taxId: [] as Array<{ Tin: string; Tinname: string }>,
  npi: { Value: 'All', Name: 'All' },
  specialty: [] as FilterOption[],
  groups: 'All',
};

// ─────────────────────────────────────────────────────────
//  UI Labels
// ─────────────────────────────────────────────────────────

// Panel Header
export const GLOBAL_FILTER_HEADER_TITLE = 'Filters';

// Filter Section Titles
export const TIME_PERIOD_TITLE = 'Time Period';
export const TAX_ID_TITLE = 'Tax ID';
export const CONTRACT_ID_TITLE = 'Contract ID';
export const LINE_OF_BUSINESS_TITLE = 'Line of Business';
export const PROVIDER_TYPE_TITLE = 'Provider Type';
export const INDIVIDUAL_PROVIDER_TITLE = 'Individual Provider';
export const SPECIALTY_TITLE = 'Specialty';
export const CHANNEL_TITLE = 'Channel';
export const SERVICE_TYPE_TITLE = 'Service Type';
export const SERVICE_CATEGORY_TITLE = 'Service Category';
export const GROUPS_TITLE = 'Groups';

// Footer Buttons
export const RESET_FILTERS_BUTTON = 'Reset Filters';
export const APPLY_FILTERS_BUTTON = 'Apply Filters';

// GlobalFilterButton
export const FILTER_BUTTON_LABEL = 'Filters';

// GlobalFilterChips Labels
export const TAX_ID_CHIP_LABEL = 'Tax ID:';
export const PROVIDER_TYPE_CHIP_LABEL = 'Provider Type:';
export const CHANNEL_CHIP_LABEL = 'Channel:';
export const SERVICE_CATEGORY_CHIP_LABEL = 'Service Category:';
export const SPECIALTY_CHIP_LABEL = 'Specialties:';
export const REGION_CHIP_LABEL = 'Region:';
export const STATE_CHIP_LABEL = 'State:';
export const MARKET_CHIP_LABEL = 'Market:';
export const SPECIALTY_CHIPS_VISIBLE_LIMIT = 5;
export const VIEW_ALL_LABEL = 'View All';

// Error Messages
export const ERROR_LOADING_FILTERS = 'Error loading filter settings';
export const ERROR_SAVING_FILTERS = 'Error saving filter settings';
export const ERROR_APPLYING_FILTERS = 'Error applying filters';
export const ERROR_RESETTING_FILTERS = 'Error resetting filters to defaults';
export const ERROR_READING_SESSION_STORAGE = 'Error reading filter data from session';
export const ERROR_WRITING_SESSION_STORAGE = 'Error writing filter data to session';

// Console Log Messages
export const LOG_FILTER_CHANGE_DETECTED =
  '[usePriorAuthFilters] Global filter change detected, syncing filters';
export const LOG_STORAGE_CHANGE_DETECTED =
  '[usePriorAuthFilters] Storage change detected, reloading from session';
export const LOG_EVENT_DETECTED = '[usePriorAuthFilters] pageFiltersChanged event detected';
export const LOG_APPLYING_FILTERS = '[GlobalFilterPanel] Applying filters:';
export const LOG_RESETTING_FILTERS = '[GlobalFilterPanel] Resetting filters';
export const LOG_EXTERNAL_FILTER_CHANGE =
  '[usePriorAuthFilters] External filter change detected, reloading from session';

// ─────────────────────────────────────────────────────────
//  Filter Configuration
// ─────────────────────────────────────────────────────────

export const getDefaultFilters = (_pageName: PageName): FilterState => {
  return { ...DEFAULT_FILTERS };
};

export const getFilterConfig = (pageName: PageName): FilterConfig => {
  return {
    page: pageName,
    enabledFilters: [
      'timePeriod',
      'taxId',
      'lineOfBusiness',
      'providerSetting',
      'npi',
      'specialty',
      'channel',
      'serviceSetting',
      'serviceCategory',
    ],
    defaultValues: getDefaultFilters(pageName),
  };
};
